import { useState } from 'react';
import { AnimatePresence } from 'motion/react';
import type { IframePoolItem, JustPulledItem } from '../../../modules/api';
import { Close, SheetFrame } from '../../../modules/design-system';
import { JustPulledStrip } from '../JustPulled';
import { HitSheet } from '../PotentialHits';
import { HitTile } from './HitTile';

type Props = {
  potentialHits: IframePoolItem[];
  justPulledItems: JustPulledItem[];
  justPulledLoading: boolean;
  onShowJustPulled?: () => void;
  onClose: () => void;
};

const TITLE_ID = 'glass-whats-inside-title';

/**
 * The phone's "What's inside?" sheet (Figma 19101:336895): full height, the
 * Just pulled strip over the potential hits, three to a row. A glass card
 * 6 off the screen edges with all corners rounded (repackz-handoff §7.4).
 */
export function WhatsInsideSheet({ potentialHits, justPulledItems, justPulledLoading, onShowJustPulled, onClose }: Props) {
  const [selected, setSelected] = useState<IframePoolItem | null>(null);

  return (
    <SheetFrame
      onClose={onClose}
      ariaLabelledBy={TITLE_ID}
      inspectName="WhatsInsideSheet"
      panelClassName={
        '!inset-x-[6px] !bottom-[calc(6px+var(--page-safe-bottom,0px))] h-[calc(100%-var(--page-safe-top,0px)-24px)] !px-4 ' +
        'rounded-[32px] border-[0.5px] border-solid [border-color:var(--glass-panel-rim)] ' +
        '[background:var(--glass-sheet-fill)] [backdrop-filter:blur(var(--glass-panel-blur))]'
      }
      handleClassName="[background:var(--glass-sheet-handle)]"
    >
      <header className="relative flex h-9 shrink-0 items-center justify-center">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute left-0 flex size-9 cursor-pointer items-center justify-center rounded-full border-[0.5px] border-solid text-white [border-color:var(--glass-panel-rim)] [background:var(--glass-close-fill)]"
        >
          <Close size={12} />
        </button>
        <h2 id={TITLE_ID} className="text-[15px] font-semibold leading-5 tracking-[-0.25px] text-white">
          {"What's inside?"}
        </h2>
      </header>

      <div className="mt-4 flex flex-col gap-4">
        <JustPulledStrip
          items={justPulledItems}
          loading={justPulledLoading}
          {...(onShowJustPulled ? { onShowAll: onShowJustPulled } : {})}
        />

        {potentialHits.length > 0 && (
          <section className="flex flex-col gap-3">
            <h3 className="text-[13px] font-bold leading-[19px] [color:var(--glass-text-muted)]">Potential Hits</h3>
            <div className="grid grid-cols-3 gap-2">
              {potentialHits.map((item) => (
                <HitTile key={item.id} item={item} onClick={setSelected} />
              ))}
            </div>
          </section>
        )}
      </div>

      <AnimatePresence>
        {selected && <HitSheet item={selected} onClose={() => setSelected(null)} />}
      </AnimatePresence>
    </SheetFrame>
  );
}
